import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Chip } from './PathwayIntelligencePage';
import {
  ArrowLeft, BookOpen, Briefcase, Cpu, Layers, Rocket, Bookmark, Info, ChevronDown,
  History, Activity, Compass, MapPin, Building2, TrendingUp, AlertTriangle, Star, Printer,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });
const money = (n) => (n == null ? '-' : `$${Math.round(n).toLocaleString()}`);
const v = (d) => (d && typeof d === 'object' && 'value' in d ? d.value : d);

const RELATION = {
  direct: { hue: '#22d3ee', label: 'Direct', note: 'A strong, established line from this programme to the job.' },
  adjacent: { hue: '#a78bfa', label: 'Adjacent', note: 'The degree is a useful foundation; expect to add skills or experience.' },
  stretch: { hue: '#fbbf24', label: 'Stretch', note: 'Possible, but it takes meaningful extra training or specialisation.' },
};
const AI_HUE = {
  'Low Transformation': '#34d399', 'Moderate Transformation': '#38bdf8',
  'High Transformation': '#fb7185', 'Emerging Opportunity': '#a78bfa',
};
const VALUE_HUE = { strong: '#34d399', typical: '#38bdf8', caution: '#fb7185' };

/* ── small pieces ───────────────────────────────────────────────────────── */

const Stat = ({ label, value, hue, sub }) => (
  <div className="rounded-xl border p-4" style={{ borderColor: `${hue}33`, background: `${hue}0a` }}>
    <p className="font-outfit text-2xl font-bold" style={{ color: hue }}>{value}</p>
    <p className="mt-0.5 text-[11px] text-slate-300">{label}</p>
    {sub && <p className="mt-0.5 text-[10.5px] text-slate-300">{sub}</p>}
  </div>
);

const WageBar = ({ p25, median, p75, p90, max }) => {
  if (!median) return null;
  const ceiling = max || p90 || p75 || median * 1.6;
  const pct = (n) => Math.min(100, Math.max(0, (n / ceiling) * 100));
  return (
    <div className="mt-2">
      <div className="relative h-2 rounded-full bg-white/[0.06]">
        {p25 && p75 && (
          <div className="absolute h-2 rounded-full bg-gradient-to-r from-cyan-500/70 to-indigo-400/70"
            style={{ left: `${pct(p25)}%`, width: `${Math.max(pct(p75) - pct(p25), 3)}%` }} />
        )}
        <div className="absolute -top-0.5 h-3 w-0.5 rounded bg-cyan-200 shadow-[0_0_10px_#22d3ee]"
          style={{ left: `${pct(median)}%` }} />
      </div>
      <div className="mt-1.5 flex justify-between text-[10.5px] text-slate-300">
        <span>{p25 ? `${money(p25)} (25th)` : ''}</span>
        <span className="text-cyan-200">{money(median)} median</span>
        <span>{p75 ? `${money(p75)} (75th)` : ''}</span>
      </div>
    </div>
  );
};

const Meter = ({ label, value, hue }) => (
  <div>
    <div className="flex justify-between text-[11px] text-slate-300"><span>{label}</span><span>{value}%</span></div>
    <div className="mt-1 h-1.5 rounded-full bg-white/[0.06]">
      <div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${value}%`, background: hue }} />
    </div>
  </div>
);

const AiPanel = ({ ai }) => {
  const d = v(ai);
  if (!d) return null;
  const hue = AI_HUE[d.outlook] || '#94a3b8';
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: `${hue}44`, background: `${hue}0d` }} data-testid="ai-panel">
      <div className="flex items-center gap-2">
        <Cpu className="h-4 w-4" style={{ color: hue }} />
        <p className="text-sm font-semibold" style={{ color: hue }}>{d.outlook}</p>
        <span className="ml-auto text-[11px] text-slate-300">Exposure {d.exposure}/100</span>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Meter label="Likely to be automated" value={d.automation_potential} hue="#fb7185" />
        <Meter label="Likely to be augmented" value={d.augmentation_potential} hue="#34d399" />
      </div>
      {!!d.human_advantage?.length && (
        <p className="mt-3 text-[11.5px] text-slate-300"><span className="text-slate-300">Stays human: </span>{d.human_advantage.join(' · ')}</p>
      )}
      {!!d.emerging_skills?.length && (
        <p className="mt-1.5 text-[11.5px] text-slate-300"><span className="text-slate-300">Rising in value: </span>{d.emerging_skills.join(' · ')}</p>
      )}
      <p className="mt-3 text-[10.5px] leading-relaxed text-slate-300">
        {d.rationale} A modelled, forward-looking view, not a measurement, and not a prediction that any job disappears.
      </p>
    </div>
  );
};

const CareerCard = ({ c, onSave }) => {
  const [open, setOpen] = useState(false);
  const rel = RELATION[c.relationship] || RELATION.direct;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 transition-all duration-200 hover:border-cyan-300/30
      hover:shadow-[0_0_36px_-20px_#22d3ee]" data-testid={`career-${c.soc_code}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-white">{c.title}</p>
          <p className="mt-0.5 text-[11px] text-slate-300">SOC {c.soc_code} · Typically enters with {c.entry_education}</p>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <Chip label={rel.label} hue={rel.hue} />
          {c.opportunity_score && <Chip label={`Opportunity ${v(c.opportunity_score)}`} hue="#22d3ee" />}
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-300">{c.earnings_statement}</p>
      <WageBar p25={v(c.wages?.p25)} median={v(c.wages?.median)} p75={v(c.wages?.p75)} p90={v(c.wages?.p90)} />
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
        {c.ai_impact && <Chip label={v(c.ai_impact).outlook} hue={AI_HUE[v(c.ai_impact).outlook] || '#94a3b8'} />}
        {c.outlook && (
          <span className="text-slate-300">
            Outlook {v(c.outlook?.growth_pct) > 0 ? '+' : ''}{v(c.outlook?.growth_pct)}% ·
            {' '}{Number(v(c.outlook?.annual_openings) || 0).toLocaleString()} openings a year
          </span>
        )}
        <button onClick={() => setOpen(!open)} className="ml-auto inline-flex items-center gap-1 text-cyan-300"
          data-testid={`career-expand-${c.soc_code}`}>
          {open ? 'Less' : 'Why this, and how AI changes it'}
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-[11.5px] text-slate-300">{rel.note} Mapping evidence: {c.evidence}.</p>
          <AiPanel ai={c.ai_impact} />
          <button onClick={() => onSave(c)} data-testid={`save-career-${c.soc_code}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-300/40 px-3 py-1.5 text-[11px] text-cyan-200 hover:bg-cyan-400/10">
            <Bookmark className="h-3 w-3" /> Save this as a pathway
          </button>
        </div>
      )}
    </div>
  );
};

/* ── page ───────────────────────────────────────────────────────────────── */

export default function MajorIntelligencePage() {
  const { cipCode } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const stateFilter = params.get('state') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [levelFilter, setLevelFilter] = useState('');
  const [colleges, setColleges] = useState([]);
  const [collegeSort, setCollegeSort] = useState('earnings');
  const [geo, setGeo] = useState([]);
  const [tab, setTab] = useState('direct');
  const [majors, setMajors] = useState([]);
  const [second, setSecond] = useState('');
  const [kind, setKind] = useState('minor');
  const [combo, setCombo] = useState(null);
  const [saved, setSaved] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = stateFilter ? `?state=${stateFilter}` : '';
      const [d, m, st, g] = await Promise.all([
        axios.get(`${API}/pathways/majors/${cipCode}${q}`, auth()),
        axios.get(`${API}/pathways/majors?q=`, auth()),
        axios.get(`${API}/pathways/states`, auth()),
        axios.get(`${API}/pathways/majors/${cipCode}/states`, auth()),
      ]);
      setData(d.data);
      setMajors((m.data.results || []).filter((x) => x.cip4 !== cipCode));
      setStates(st.data.states || []);
      setGeo(g.data.states || []);
    } finally { setLoading(false); }
  }, [cipCode, stateFilter]);

  const loadColleges = useCallback(async () => {
    const p = new URLSearchParams({ sort: collegeSort, limit: '40' });
    if (stateFilter) p.set('state', stateFilter);
    if (levelFilter) p.set('level', levelFilter);
    const r = await axios.get(`${API}/pathways/majors/${cipCode}/colleges?${p}`, auth());
    setColleges(r.data.colleges || []);
  }, [cipCode, stateFilter, levelFilter, collegeSort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadColleges(); }, [loadColleges]);

  const runCombine = async () => {
    if (!second) return;
    const r = await axios.post(`${API}/pathways/combine`, { cip_a: cipCode, cip_b: second, kind }, auth());
    setCombo(r.data);
  };

  const savePathway = async (career) => {
    await axios.post(`${API}/pathways/saved`, {
      pathway_type: 'major',
      pathway_name: career ? `${data.major.title} → ${career.title}` : data.major.title,
      cip_code: cipCode, soc_code: career?.soc_code,
    }, auth());
    setSaved(career ? career.title : data.major.title);
    setTimeout(() => setSaved(''), 3500);
  };

  if (loading) return <AppLayout><LoadingSpinner /></AppLayout>;
  if (!data) return <AppLayout><p className="text-slate-300">That programme could not be found.</p></AppLayout>;

  const { major, levels, careers, career_counts: counts, program_opportunity: opp, your_reading: reading } = data;
  const gap = v(reading.gap.skill_gap) || {};
  const readiness = v(reading.gap.transition_readiness) || {};
  const earning = levels.filter((l) => v(l.median_earnings));
  const ceiling = Math.max(...earning.map((l) => v(l.median_earnings)), 1);
  const bachelors = earning.find((l) => l.level === 3);
  const masters = earning.find((l) => l.level === 5);
  const step = bachelors && masters ? v(masters.median_earnings) - v(bachelors.median_earnings) : null;

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="major-intelligence-page">
        <div className="sticky top-0 z-20 -mx-2 flex flex-wrap items-center gap-3 rounded-xl border border-white/10
          bg-[#050c1c]/85 px-4 py-2.5 backdrop-blur-xl print:hidden" data-testid="major-action-bar">
          <button onClick={() => navigate('/app/pathways')} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-cyan-300"
            data-testid="back-to-pathways">
            <ArrowLeft className="h-3.5 w-3.5" /> Pathway Intelligence
          </button>
          <span className="hidden truncate text-xs font-medium text-white sm:inline">{data.major.title}</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-cyan-300" />
            <select value={stateFilter} data-testid="scope-state"
              onChange={(e) => setParams(e.target.value ? { state: e.target.value } : {})}
              className="rounded-lg border border-white/10 bg-[#0b1220] px-3 py-1.5 text-xs text-white">
              <option value="">Nationally</option>
              {states.map((s) => <option key={s} value={s}>{s} only</option>)}
            </select>
            <button onClick={() => window.print()} data-testid="print-major"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-1.5 text-xs text-slate-300
                transition-colors hover:border-cyan-300/40 hover:text-cyan-100">
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
            <button onClick={() => savePathway(null)} data-testid="save-major-top"
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-indigo-500 px-3 py-1.5
                text-xs font-semibold text-white shadow-[0_0_26px_-10px_#22d3ee] transition-transform hover:-translate-y-0.5">
              <Bookmark className="h-3.5 w-3.5" /> Save
            </button>
          </div>
        </div>
        {saved && (
          <div className="rounded-lg border border-cyan-300/30 bg-cyan-400/[0.08] px-3 py-2 text-[11.5px] text-cyan-100"
            data-testid="save-toast">Saved “{saved}” to your pathways</div>
        )}

        {/* ── Executive view ── */}
        <GlassCard className="relative overflow-hidden p-6">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.16), transparent 65%)' }} />
          <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300">
            CIP {major.cip_code} · {major.institution_count.toLocaleString()} institutions · {major.states.length} states
          </p>
          <h1 className="font-outfit mt-1 text-3xl font-semibold text-white sm:text-4xl" data-testid="major-title">{major.title}</h1>
          <p className="mt-1 text-xs text-slate-300">
            Showing federal outcomes {data.scope.label}
            {major.awards ? ` · ${major.awards.toLocaleString()} credentials awarded a year` : ''}
          </p>

          <div className="mt-6 grid gap-4 lg:grid-cols-4">
            <div className="rounded-xl border p-4" style={{ borderColor: '#22d3ee44', background: '#22d3ee0d' }}>
              <div className="flex items-baseline gap-2">
                <span className="font-outfit text-3xl font-bold text-cyan-300">{v(opp) ?? '-'}</span>
                <span className="text-xs text-slate-300">/ 100</span>
              </div>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-300">Programme opportunity</p>
              <p className="mt-1.5 text-[11px] leading-snug text-slate-300">
                Market evidence across {counts.direct + counts.adjacent + counts.stretch} mapped occupations.
                Your personal fit is scored separately.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3 lg:col-span-3">
              <Stat label="Direct careers" value={counts.direct} hue="#22d3ee" />
              <Stat label="Adjacent careers" value={counts.adjacent} hue="#a78bfa" />
              <Stat label="Stretch careers" value={counts.stretch} hue="#fbbf24" />
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:col-span-3">
                <p className="text-[11px] uppercase tracking-wider text-slate-300">Where you stand today</p>
                <p className="mt-1.5 text-sm text-white">
                  {readiness.band === 'Ready' ? 'You already hold most of what this path expects.'
                    : readiness.band === 'Close' ? 'You are part of the way there.'
                    : 'This is an early-stage path for you, most of the skills are still ahead.'}
                  {' '}<span className="text-cyan-300">{gap.match_percent ?? 0}% skills match</span>
                  {readiness.score != null && <span className="text-slate-300"> · readiness {readiness.score}/100</span>}
                </p>
                {!!gap.missing?.length && (
                  <p className="mt-1.5 text-[11.5px] text-slate-300">Still to build: {gap.missing.slice(0, 5).join(' · ')}</p>
                )}
              </div>
            </div>
          </div>
          <p className="mt-4 flex items-start gap-2 text-[11px] leading-relaxed text-slate-300">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {data.how_to_read_this}
          </p>
        </GlassCard>

        {/* ── What this field actually is ── */}
        {(major.definition || major.specializations?.length > 0) && (
          <GlassCard className="p-6" data-testid="definition-panel">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-cyan-300" />
              <p className="text-sm font-semibold text-white">What this field actually is</p>
              <span className="ml-auto text-[11px] text-slate-300">NCES CIP 2020 definition</span>
            </div>
            {major.definition && <p className="mt-3 text-sm leading-relaxed text-slate-300">{major.definition}</p>}
            {!!major.specializations?.length && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider text-slate-300">
                  {major.specialization_count} specialisations inside this field
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {major.specializations.map((sp) => (
                    <span key={sp.code} title={sp.examples?.join(', ')}
                      className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-300
                        transition-colors hover:border-cyan-300/40 hover:text-cyan-100">
                      {sp.title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>
        )}

        {/* ── The credential ladder ── */}
        <GlassCard className="p-6 shadow-[0_0_60px_-40px_#22d3ee]" data-testid="credential-ladder">
          <div className="flex flex-wrap items-center gap-2">
            <TrendingUp className="h-4 w-4 text-cyan-300" />
            <p className="text-sm font-semibold text-white">What each level actually pays</p>
            <span className="ml-auto text-[11px] text-slate-300">Median earnings of graduates · College Scorecard</span>
          </div>
          <div className="mt-4 space-y-3">
            {earning.map((l) => {
              const m = v(l.median_earnings);
              const ratio = l.median_debt && m ? l.median_debt / m : null;
              return (
                <div key={l.level} className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                  data-testid={`level-${l.level}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-white">{l.label}</p>
                    <p className="text-xs text-slate-300">
                      {l.institutions.toLocaleString()} institutions{l.awards ? ` · ${l.awards.toLocaleString()} awarded a year` : ''}
                    </p>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-white/[0.06]">
                    <div className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-400 shadow-[0_0_18px_-4px_#22d3ee]
                      transition-all duration-700" style={{ width: `${(m / ceiling) * 100}%` }} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[11.5px]">
                    <span className="text-cyan-200">{money(m)} median, four years out</span>
                    {l.early_earnings && <span className="text-slate-300">{money(l.early_earnings)} in year one</span>}
                    {l.median_debt && <span className="text-slate-300">{money(l.median_debt)} median debt</span>}
                    {ratio != null && (
                      <span style={{ color: ratio <= 0.5 ? '#34d399' : ratio > 1 ? '#fb7185' : '#94a3b8' }}>
                        debt is {Math.round(ratio * 100)}% of a year's pay
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {!earning.length && (
              <p className="text-sm text-slate-300">
                The federal data does not publish earnings for this programme at the level of detail needed
                {stateFilter ? ` in ${stateFilter}` : ''}. Try widening the scope.
              </p>
            )}
          </div>
          {step != null && (
            <p className="mt-4 rounded-xl border border-violet-400/25 bg-violet-500/[0.07] p-3 text-[11.5px] text-slate-200">
              <span className="text-violet-200">The master's step: </span>
              graduates with a master's in this field earn about {money(Math.abs(step))} {step >= 0 ? 'more' : 'less'} a year than
              bachelor's graduates{masters.median_debt ? `, and borrow about ${money(masters.median_debt)} to get it` : ''}.
              {step > 0 && masters.median_debt
                ? ` At that gap, the extra borrowing pays for itself in roughly ${(masters.median_debt / step).toFixed(1)} years.`
                : ''}
            </p>
          )}
        </GlassCard>

        {/* ── Colleges ── */}
        <GlassCard className="p-6 shadow-[0_0_60px_-40px_#38bdf8]" data-testid="colleges-panel">
          <div className="flex flex-wrap items-center gap-3">
            <Building2 className="h-4 w-4 text-cyan-300" />
            <p className="text-sm font-semibold text-white">
              Colleges offering this {stateFilter ? `in ${stateFilter}` : 'across the country'}
            </p>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} data-testid="colleges-level"
                className="rounded-lg border border-white/10 bg-[#0b1220] px-3 py-1.5 text-xs text-white">
                <option value="">Every level</option>
                {levels.map((l) => <option key={l.level} value={l.level}>{l.label}</option>)}
              </select>
              <select value={collegeSort} onChange={(e) => setCollegeSort(e.target.value)} data-testid="colleges-sort"
                className="rounded-lg border border-white/10 bg-[#0b1220] px-3 py-1.5 text-xs text-white">
                <option value="earnings">Highest graduate earnings</option>
                <option value="net_price">Lowest net price</option>
                <option value="awards">Most graduates</option>
                <option value="name">Name</option>
              </select>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-white/10 text-[10.5px] uppercase tracking-wider text-slate-300">
                  <th className="pb-2 pr-3">Institution</th>
                  <th className="pb-2 pr-3">Level</th>
                  <th className="pb-2 pr-3">Graduate earnings</th>
                  <th className="pb-2 pr-3">Median debt</th>
                  <th className="pb-2 pr-3">Net price</th>
                  <th className="pb-2">Debt vs pay</th>
                </tr>
              </thead>
              <tbody>
                {colleges.map((c, i) => (
                  <tr key={`${c.unitid}-${c.credential_level}-${i}`}
                    className="border-b border-white/[0.05] transition-colors hover:bg-cyan-400/[0.04]"
                    data-testid={`college-row-${c.unitid}-${c.credential_level}`}>
                    <td className="py-2.5 pr-3">
                      <p className="text-xs font-medium text-white">{c.institution}</p>
                      <p className="text-[10.5px] text-slate-300">
                        {c.city}, {c.state} · {c.ownership}{c.hbcu ? ' · HBCU' : ''}
                      </p>
                    </td>
                    <td className="pr-3 text-[11px] text-slate-300">{c.credential_title}</td>
                    <td className="pr-3 text-[11px] text-cyan-200">{money(c.earnings_4yr)}</td>
                    <td className="pr-3 text-[11px] text-slate-300">
                      {money(c.debt_shown)}
                      {c.debt_basis === 'institution' && <span className="text-slate-300" title="Institution-wide median; the federal data does not publish debt for this specific programme"> *</span>}
                    </td>
                    <td className="pr-3 text-[11px] text-slate-300">{money(c.net_price)}</td>
                    <td className="text-[11px]">
                      {c.value_flag ? (
                        <span className="inline-flex items-center gap-1" style={{ color: VALUE_HUE[c.value_flag] }}>
                          {c.value_flag === 'strong' && <Star className="h-3 w-3" />}
                          {c.value_flag === 'caution' && <AlertTriangle className="h-3 w-3" />}
                          {Math.round(c.debt_to_earnings * 100)}%
                        </span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!colleges.length && <p className="py-4 text-sm text-slate-300">No institutions match those filters.</p>}
          </div>
          <p className="mt-3 text-[10.5px] text-slate-300">
            Earnings are the federal number for that exact programme at that exact school. Debt marked * is the
            institution-wide median, because the federal data does not publish borrowing for every individual programme.
            "Debt vs pay" is median borrowing as a share of one year of graduate earnings, under 50% is comfortable,
            over 100% deserves a hard look.
          </p>
        </GlassCard>

        {/* ── Geography ── */}
        {!!geo.length && (
          <GlassCard className="p-6 shadow-[0_0_60px_-40px_#2dd4bf]" data-testid="geography-panel">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-teal-300" />
              <p className="text-sm font-semibold text-white">Where this degree pays most</p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {geo.slice(0, 8).map((g) => (
                <button key={g.state} onClick={() => setParams({ state: g.state })} data-testid={`geo-${g.state}`}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-left transition-all hover:-translate-y-0.5
                    hover:border-teal-300/40 hover:shadow-[0_0_30px_-18px_#2dd4bf]">
                  <p className="text-sm font-semibold text-white">{g.state}</p>
                  <p className="mt-0.5 text-[11.5px] text-teal-200">{money(g.median_earnings)}</p>
                  <p className="text-[10.5px] text-slate-300">{g.institutions} institutions{g.median_debt ? ` · ${money(g.median_debt)} debt` : ''}</p>
                </button>
              ))}
            </div>
            <p className="mt-3 text-[10.5px] text-slate-300">Ranked by graduate earnings. Cost of living differs by state, a
              higher number is not automatically a better life.</p>
          </GlassCard>
        )}

        {/* ── Careers ── */}
        {(counts.direct + counts.adjacent + counts.stretch) > 0 ? (
          <GlassCard className="p-6">
            <div className="flex flex-wrap items-center gap-3">
              <Briefcase className="h-4 w-4 text-cyan-300" />
              <p className="text-sm font-semibold text-white">Where this programme leads</p>
              <div className="ml-auto flex gap-1.5">
                {['direct', 'adjacent', 'stretch'].map((k) => (
                  <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                    className="rounded-full border px-3 py-1 text-[11px] capitalize transition-colors"
                    style={{
                      borderColor: tab === k ? `${RELATION[k].hue}88` : 'rgba(148,163,184,0.2)',
                      background: tab === k ? `${RELATION[k].hue}14` : 'transparent',
                      color: tab === k ? RELATION[k].hue : '#cbd5e1',
                    }}>{k} ({counts[k]})</button>
                ))}
              </div>
            </div>
            <p className="mt-2 text-[11.5px] text-slate-300">{RELATION[tab].note}</p>
            <div className="mt-4 space-y-3">
              {careers[tab].map((c) => <CareerCard key={c.soc_code} c={c} onSave={savePathway} />)}
              {!careers[tab].length && <p className="text-sm text-slate-300">Nothing mapped at this level yet.</p>}
            </div>
          </GlassCard>
        ) : (
          <GlassCard className="p-6">
            <p className="text-sm text-white">Occupation mapping for this programme is still being ingested.</p>
            <p className="mt-1 text-[11.5px] text-slate-300">
              Earnings, debt and institutions above are complete federal data. The CIP-to-occupation crosswalk is
              being expanded across all 1,700+ programmes, this page will fill in as it lands.
            </p>
          </GlassCard>
        )}

        {/* ── Then, now, next ── */}
        {!!careers.direct.length && careers.direct[0].wages && (
          <GlassCard className="p-6">
            <p className="text-sm font-semibold text-white">What this path has looked like, and where it is heading</p>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <Panel icon={History} hue="#94a3b8" title="What it has looked like"
                body={`Published wage history for these occupations comes from the BLS OES series. Median pay for the leading direct occupation currently sits at ${money(v(careers.direct[0]?.wages?.median))}.`} />
              <Panel icon={Activity} hue="#22d3ee" title="What it looks like today"
                body={careers.direct[0]?.earnings_statement} />
              <Panel icon={Cpu} hue="#a78bfa" title="Where it is heading"
                body={`${v(careers.direct[0]?.ai_impact)?.outlook || 'AI assessment pending for this occupation'}. Rising in value: ${(careers.direct[0]?.emerging_skills || []).slice(0, 3).join(', ') || '-'}.`} />
            </div>
          </GlassCard>
        )}

        {/* ── Combination builder ── */}
        <GlassCard className="p-6 shadow-[0_0_60px_-40px_#a78bfa]" data-testid="combination-builder">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-violet-300" />
            <p className="text-sm font-semibold text-white">Pathway combination builder</p>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-lg border border-cyan-300/30 bg-cyan-400/[0.08] px-3 py-2 text-xs text-cyan-100">{major.title}</span>
            <span className="text-slate-300">+</span>
            <select value={second} onChange={(e) => setSecond(e.target.value)} data-testid="combine-second"
              className="max-w-[280px] rounded-lg border border-white/10 bg-[#0b1220] px-3 py-2 text-xs text-white">
              <option value="">Choose a second field…</option>
              {majors.slice(0, 60).map((m) => <option key={m.cip4} value={m.cip4}>{m.title}</option>)}
            </select>
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="combine-kind"
              className="rounded-lg border border-white/10 bg-[#0b1220] px-3 py-2 text-xs text-white">
              <option value="minor">as a minor</option>
              <option value="double_major">as a double major</option>
              <option value="certificate">as a certificate</option>
              <option value="accelerated_masters">as an accelerated master's (4+1)</option>
            </select>
            <button onClick={runCombine} data-testid="run-combine"
              className="rounded-lg bg-gradient-to-r from-violet-500 to-cyan-500 px-4 py-2 text-xs font-semibold text-white
                shadow-[0_0_28px_-12px_#a78bfa] transition-transform hover:-translate-y-0.5">Combine</button>
          </div>
          {combo && (
            <div className="mt-5 space-y-4" data-testid="combination-result">
              <p className="text-sm text-white">{combo.combination}</p>
              <div className="grid gap-3 sm:grid-cols-4">
                <Stat label="Occupations reached" value={combo.occupations_total} hue="#22d3ee" />
                <Stat label="Newly unlocked" value={combo.additional_occupations_unlocked.length} hue="#a78bfa" />
                <Stat label="Extra credits" value={combo.additional_credits} hue="#fbbf24" />
                <Stat label="AI resilience" value={v(combo.ai_resilience)} hue="#34d399" />
              </div>
              {!!combo.additional_occupations_unlocked.length && (
                <p className="text-[11.5px] text-slate-300"><span className="text-slate-300">Opens up: </span>
                  {combo.additional_occupations_unlocked.map((c) => c.title).join(' · ')}</p>
              )}
              {!!combo.new_skill_combination.length && (
                <p className="text-[11.5px] text-slate-300"><span className="text-slate-300">Adds the skills: </span>
                  {combo.new_skill_combination.join(' · ')}</p>
              )}
              <p className="text-[11px] text-slate-300">Time impact: +{combo.time_impact_years} years · Cost impact: {combo.cost_impact}</p>
              <p className="text-[10.5px] leading-relaxed text-slate-300">{combo.caveat}</p>
            </div>
          )}
        </GlassCard>

        {/* ── Entrepreneurship ── */}
        {!!data.ventures?.length && (
          <GlassCard className="p-6" data-testid="venture-panel">
            <div className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-amber-300" />
              <p className="text-sm font-semibold text-white">If you want to own the business instead</p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.ventures.map((b) => (
                <span key={b} className="rounded-full border border-amber-300/30 bg-amber-400/[0.07] px-3 py-1.5 text-xs text-amber-100">{b}</span>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-300">
              Business categories this education plausibly supports, not a claim about revenue or success.
            </p>
          </GlassCard>
        )}

        {/* ── Build my FuturePath ── */}
        <GlassCard className="p-6" data-testid="build-plan">
          <div className="flex items-center gap-2">
            <Compass className="h-4 w-4 text-cyan-300" />
            <p className="text-sm font-semibold text-white">Build my FuturePath</p>
            <span className="ml-auto text-[11px] text-slate-300">
              {reading.action_plan.horizon === 'school' ? 'Grade by grade' : 'Next 24 months'}
            </span>
          </div>
          <div className="mt-4 space-y-2">
            {reading.action_plan.steps.map((s, i) => (
              <div key={s.when} className="flex gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-3
                transition-colors hover:border-cyan-300/25">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-400/15 text-[11px] font-bold text-cyan-200">{i + 1}</span>
                <div>
                  <p className="text-xs font-semibold text-white">{s.when}</p>
                  <p className="text-[11.5px] text-slate-300">{s.actions.join(' · ')}</p>
                </div>
              </div>
            ))}
          </div>
          {!!reading.risks?.length && (
            <div className="mt-4 rounded-xl border border-rose-400/25 bg-rose-500/[0.06] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-200">What could make this harder</p>
              <ul className="mt-1.5 space-y-1">
                {reading.risks.map((r) => <li key={r} className="text-[11.5px] text-slate-300">· {r}</li>)}
              </ul>
            </div>
          )}
          <button onClick={() => savePathway(null)} data-testid="save-major-pathway"
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-indigo-500 px-4 py-2 text-xs
              font-semibold text-white shadow-[0_0_28px_-10px_#22d3ee] transition-transform hover:-translate-y-0.5">
            <Bookmark className="h-3.5 w-3.5" /> Save this pathway
          </button>
          {saved && <span className="ml-3 text-[11px] text-cyan-300" data-testid="save-confirm">Saved “{saved}” to your pathways</span>}
        </GlassCard>

        <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4" data-testid="citations">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">Where these numbers come from</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {Object.entries(data.citations || {}).map(([name, url]) => (
              <span key={name} className="text-[11px] text-slate-300">
                {url ? <a href={url} target="_blank" rel="noreferrer" className="hover:text-cyan-300 hover:underline">{name}</a> : name}
              </span>
            ))}
          </div>
          <p className="mt-2 text-[10.5px] text-slate-300">
            Programme earnings, debt and institutions are published federal data. Occupational wages and outlook are BLS.
            Opportunity and AI transformation are our own models, labelled as modelled, with components shown.
            Nothing on this page is generated by a language model.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}

const Panel = ({ icon: Icon, hue, title, body }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
    <Icon className="h-4 w-4" style={{ color: hue }} />
    <p className="mt-2 text-xs font-semibold text-white">{title}</p>
    <p className="mt-1.5 text-[11.5px] leading-relaxed text-slate-300">{body}</p>
  </div>
);
